import { Link } from 'react-router-dom'
import { ArrowLeft, Lock, Mail, ShieldCheck } from 'lucide-react'
const sections = [
  {
    title: 'Introduction',
    bullets: [
      'Welcome to HomiCareplus Technologies Limited.',
      'HomiCareplus is a Nigerian company registered under the laws of the Federal Republic of Nigeria with its registered office in 28 Joseph Avenue, Sangotedo, Lagos - Nigeria.',
      'HomiCareplus provides a platform that connects patients with licensed healthcare professionals for in-home healthcare services.',
      'HomiCareplus Technologies Limited (“HomiCareplus”, “us”, “we”, or “our”) owns and operates the website under the domain www.homicareplus.com, the web app: app.homicareplus.com and HomiCareplus mobile application (hereinafter referred to as “Service”).',
      'Our Privacy Policy governs your use of our service and explains how we collect, safeguard and disclose information that results from your use of our Service.',
      'We use your data to provide and improve Service. By using Service, you agree to the collection and use of information in accordance with this policy. Unless otherwise defined in this Privacy Policy, the terms used in this Privacy Policy have the same meanings as in our Terms and Conditions.',
      'Our Terms and Conditions (“Terms”) govern all use of our Service and together with the Privacy Policy constitute your agreement with us (“agreement”).',
    ],
  },
  {
    title: 'Why We Collect Your Information',
    bullets: [
      'We collect and process your data to securely provide you with the Services and Additional Services. Additionally, we process data to ensure and improve the functioning of the Website, Additional Services, and the App. We collect and process your personal data, (i.e. information that identifies, or at least makes it possible to identify, you as a natural person) when you voluntarily decide to actively communicate with us, or decide to leave us your feedback regarding our Service. Furthermore, we collect and process certain technical data generated due to you visiting the Website, Additional Services, or using the App, which may also be considered personal data. \'Processing\' means any operation which is performed on personal data, such as collection, recording, organization, structuring, storage, adaptation, retrieval, any kind of disclosure, erasure or destruction, or other use.',
    ],
  },
  {
    title: 'Information We Collect',
    bullets: [
      'Personal information such as name, email address, phone number, date of birth, and address when accounts are created or services are booked.',
      'Health-related information such as medical history, medications, care needs, and treatment preferences where relevant to service delivery.',
      'Usage data describing how the platform is used, including visited screens, selected features, and session activity.',
      'Device and technical information such as device type, browser, operating system, IP address, and related identifiers.',
      'Location information, where permitted, to help connect users with nearby healthcare professionals.',
    ],
  },
  {
    title: 'How We Use Your Information',
    bullets: [
      'To deliver services, coordinate bookings, and match patients with appropriate healthcare professionals.',
      'To send confirmations, reminders, support responses, service updates, and other operational communications.',
      'To support identity verification, provider screening, fraud prevention, and platform security.',
      'To understand usage patterns and improve features, workflows, and product quality over time.',
      'To comply with legal, regulatory, and healthcare-related obligations where required.',
    ],
  },
  {
    title: 'Information Sharing',
    bullets: [
      'Relevant health and booking information may be shared with assigned healthcare professionals so they can provide appropriate care.',
      'Trusted third-party service providers may process limited information for payments, storage, infrastructure, or analytics under confidentiality obligations.',
      'Information may be disclosed where required by law, court order, regulatory obligation, or safety necessity.',
      'If the business is reorganized, merged, or transferred, user information may also be transferred subject to notice where applicable.',
      'In urgent safety situations, information may be shared with emergency responders or healthcare facilities where necessary.',
    ],
  },
  {
    title: 'Data Security',
    bullets: [
      'Information is protected using industry-standard safeguards for storage and transmission.',
      'Access controls are limited to authorized personnel and role-appropriate operational needs.',
      'Security monitoring, audits, and assessments may be conducted regularly to strengthen platform defenses.',
      'Infrastructure is designed to support secure hosting and operational monitoring.',
      'Team members handling sensitive information are expected to follow privacy and security procedures.',
    ],
  },
  {
    title: 'Your Rights',
    bullets: [
      'You may request access to the personal data we hold about you.',
      'You may request correction of inaccurate or incomplete information.',
      'You may request deletion of personal information, subject to legal and operational limitations.',
      'You may request a portable copy of eligible data in a structured format.',
      'You may opt out of certain marketing communications or non-essential processing where applicable.',
      'You may raise complaints with relevant data protection authorities if you believe your rights have been affected.',
    ],
  },
]

const complianceItems = [
  {
    title: 'HIPAA Compliance',
    body: 'HomiCareplus aims to apply privacy and security practices that support responsible handling of protected health information where HIPAA-related standards are relevant.',
  },
  {
    title: 'GDPR Compliance',
    body: 'For users in applicable jurisdictions, data practices may also reflect GDPR principles relating to lawful processing, transparency, and user rights.',
  },
  {
    title: 'NDPR Compliance',
    body: 'For Nigerian users, data handling practices are intended to align with applicable obligations under the Nigeria Data Protection Regulation.',
  },
  {
    title: 'Cross-border Transfers',
    body: 'Where data is transferred across borders, appropriate safeguards and legal transfer mechanisms are expected to be applied as required.',
  },
  {
    title: 'Local Laws',
    body: 'HomiCareplus may also comply with additional local privacy, consumer, and healthcare-related laws depending on where services are used.',
  },
]

export default function Privacy() {
  return (
    <div className="min-h-[100dvh] bg-[#fcfff8]">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={16} /> Back
        </Link>

        <div className="mt-6 rounded border border-border bg-card px-5 py-6 shadow-[0_10px_40px_rgba(15,23,42,0.04)] sm:px-8 sm:py-8">
          <img src="/HomiCare Logo.png" alt="HomiCareplus logo" className="h-10 w-auto object-contain" />

          <div className="mt-6 max-w-3xl">
            <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-[-0.03em] text-foreground">Privacy Policy</h1>
            <p className="mt-2 text-sm text-muted-foreground">Last updated: June 15, 2026</p>
          </div>
        </div>

        <div className="mt-6 space-y-5">
          {sections.map((section) => (
            <section key={section.title} className="rounded border border-border bg-card px-5 py-5 shadow-[0_10px_40px_rgba(15,23,42,0.04)] sm:px-8 sm:py-6">
              <h2 className="text-2xl font-semibold tracking-[-0.03em] text-foreground">{section.title}</h2>

              {section.bullets && (
                <ul className="mt-4 space-y-3 text-sm sm:text-base leading-7 text-muted-foreground">
                  {section.bullets.map((bullet) => (
                    <li key={bullet} className="flex gap-3">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <section className="rounded border border-border bg-card px-5 py-5 shadow-[0_10px_40px_rgba(15,23,42,0.04)] sm:px-8 sm:py-6">
            <div className="flex items-start gap-3">
              <ShieldCheck size={20} className="mt-0.5 text-primary" />
              <div className="flex-1">
                <h2 className="text-2xl font-semibold tracking-[-0.03em] text-foreground">International Compliance</h2>
                <p className="mt-3 text-sm sm:text-base leading-7 text-muted-foreground">
                  HomiCareplus may apply privacy practices designed to support healthcare and data protection requirements across multiple jurisdictions.
                </p>
                <div className="mt-5 grid gap-4">
                  {complianceItems.map((item) => (
                    <div key={item.title} className="rounded border border-border bg-background px-4 py-4">
                      <h3 className="text-lg font-semibold text-foreground">{item.title}</h3>
                      <p className="mt-2 text-sm sm:text-base leading-7 text-muted-foreground">{item.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="rounded border border-border bg-card px-5 py-5 shadow-[0_10px_40px_rgba(15,23,42,0.04)] sm:px-8 sm:py-6">
            <div className="flex items-start gap-3">
              <Lock size={20} className="mt-0.5 text-primary" />
              <div>
                <h2 className="text-2xl font-semibold tracking-[-0.03em] text-foreground">Contact Us</h2>
                <p className="mt-3 text-sm sm:text-base leading-7 text-muted-foreground">
                  If you have questions about this Privacy Policy, want to exercise your rights, or need clarification on how HomiCareplus handles data, contact us below.
                </p>

                <div className="mt-5 inline-flex items-center gap-2 rounded border border-border bg-background px-4 py-3 text-sm font-medium text-foreground">
                  <Mail size={16} className="text-primary" />
                  hello@homicareplus.com
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}